(() => {
  const overlayOpeners = new Map();
  let activeOverlay = null;
  let restoreFocus = null;

  function getFocusable(container) {
    return Array.from(container.querySelectorAll([
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(','))).filter((el) => !el.hasAttribute('hidden') && el.offsetParent !== null);
  }

  function getQuantityBounds(input) {
    const min = Number(input.getAttribute('min') || 1);
    const maxAttr = input.getAttribute('max');
    const max = maxAttr ? Number(maxAttr) : Infinity;
    return {
      min: Number.isFinite(min) ? min : 1,
      max: Number.isFinite(max) ? max : Infinity,
    };
  }

  function setButtonDisabled(button, disabled) {
    if (!button) return;
    button.disabled = disabled;
    if (disabled) {
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-disabled');
    }
  }

  function updateQuantityButtons(input) {
    const quantity = input.closest('.quantity');
    if (!quantity) return;
    const { min, max } = getQuantityBounds(input);
    const current = Number(input.value || min);
    setButtonDisabled(quantity.querySelector('[data-quantity-adjust="-1"]'), current <= min);
    setButtonDisabled(quantity.querySelector('[data-quantity-adjust="1"]'), Number.isFinite(max) && current >= max);
  }

  function clampQuantityInput(input) {
    const { min, max } = getQuantityBounds(input);
    const current = Number(input.value || min);
    const next = Math.min(max, Math.max(min, Number.isFinite(current) ? current : min));
    input.value = String(next);
    updateQuantityButtons(input);
    return next;
  }

  function getProductInventoryMax(input) {
    const rawMax = input.dataset.productInventoryMax || input.getAttribute('max');
    const max = rawMax ? Number(rawMax) : Infinity;
    return Number.isFinite(max) ? max : Infinity;
  }

  function setProductCartLimitState(input, remaining) {
    const form = input.closest('form');
    const message = form?.querySelector('[data-product-cart-limit-message]');
    const submitButton = form?.querySelector('[type="submit"][name="add"]');
    const stickyButton = document.querySelector('.product-sticky-bar__atc');
    const isSoldThroughCart = Number.isFinite(remaining) && remaining <= 0;

    input.disabled = isSoldThroughCart;
    if (Number.isFinite(remaining)) input.setAttribute('max', String(Math.max(0, remaining)));
    if (isSoldThroughCart) input.value = '0';

    [submitButton, stickyButton].forEach((button) => setButtonDisabled(button, isSoldThroughCart));
    if (message) message.hidden = !isSoldThroughCart;
    clampQuantityInput(input);
  }

  async function syncProductQuantityCartLimit(input) {
    const inventoryMax = getProductInventoryMax(input);
    if (!Number.isFinite(inventoryMax)) {
      clampQuantityInput(input);
      return Infinity;
    }

    const form = input.closest('form');
    const variantId = Number(form?.querySelector('[data-product-variant-id]')?.value);
    if (!Number.isFinite(variantId)) {
      clampQuantityInput(input);
      return inventoryMax;
    }

    const cart = await (await fetch('/cart.js')).json();
    const quantityInCart = (cart.items || []).reduce((sum, item) => {
      return item.variant_id === variantId ? sum + Number(item.quantity || 0) : sum;
    }, 0);
    const remaining = Math.max(0, inventoryMax - quantityInCart);
    setProductCartLimitState(input, remaining);
    return remaining;
  }

  function setBackgroundInert(isInert, overlay) {
    const targets = [
      document.getElementById('MainContent'),
      document.querySelector('.shopify-section-group-footer-group'),
      document.querySelector('.shopify-section-group-header-group'),
    ];

    targets.forEach((target) => {
      if (!target || (overlay && target.contains(overlay))) return;
      if (isInert) {
        target.inert = true;
      } else {
        target.inert = false;
      }
    });
  }

  function setOverlayLock(overlay) {
    activeOverlay = overlay || null;
    document.documentElement.classList.toggle('is-overlay-open', Boolean(activeOverlay));
    setBackgroundInert(Boolean(activeOverlay), activeOverlay);
  }

  function focusOverlay(overlay) {
    const panel = overlay.querySelector('[data-drawer-panel], [data-search-panel], [data-cart-panel]') || overlay;
    const target = getFocusable(panel)[0] || panel;
    if (!target.hasAttribute('tabindex') && target === panel) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  function openOverlay(overlay, opener) {
    if (!overlay) return;
    if (document.activeElement && document.activeElement !== document.body) {
      restoreFocus = document.activeElement;
    }
    overlay.hidden = false;
    if (opener) {
      overlayOpeners.set(overlay, opener);
      opener.setAttribute('aria-expanded', 'true');
    }
    setOverlayLock(overlay);
    focusOverlay(overlay);
  }

  function finishCloseOverlay(overlay, options = {}) {
    if (!overlay) return;
    overlay.hidden = true;
    const opener = overlayOpeners.get(overlay);
    if (opener) opener.setAttribute('aria-expanded', 'false');
    overlayOpeners.delete(overlay);
    if (activeOverlay === overlay) setOverlayLock(null);
    if (options.restore !== false && restoreFocus && document.contains(restoreFocus)) {
      restoreFocus.focus({ preventScroll: true });
    }
    restoreFocus = null;
  }

  function closeOverlay(overlay, options = {}) {
    finishCloseOverlay(overlay, options);
  }

  function trapFocus(event) {
    if (!activeOverlay || event.key !== 'Tab') return;
    const panel = activeOverlay.querySelector('[data-drawer-panel], [data-search-panel], [data-cart-panel]') || activeOverlay;
    const focusable = getFocusable(panel);

    if (!focusable.length) {
      event.preventDefault();
      panel.focus({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = document.querySelector(opener.getAttribute('data-drawer-open'));
      openOverlay(drawer, opener);
      return;
    }

    if (event.target.closest('[data-drawer-close]')) {
      closeOverlay(event.target.closest('[data-drawer]'));
      return;
    }

    if (event.target.closest('[data-cart-open]')) {
      const drawer = document.querySelector('[data-cart-drawer]');
      if (drawer) {
        openOverlay(drawer, event.target.closest('[data-cart-open]'));
        loadCartDrawer();
      }
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      closeOverlay(document.querySelector('[data-cart-drawer]'));
      return;
    }

    if (event.target.closest('[data-search-open]')) {
      const drawer = document.getElementById('SearchDrawer');
      if (drawer) {
        openOverlay(drawer, event.target.closest('[data-search-open]'));
        document.getElementById('SearchInput')?.focus();
      }
      return;
    }

    if (event.target.closest('[data-search-close]')) {
      closeSearchDrawer();
      return;
    }

    const quantityButton = event.target.closest('[data-quantity-adjust]');
    if (quantityButton) {
      const input = quantityButton.parentElement?.querySelector('input[type="number"]');
      if (!input) return;
      const delta = Number(quantityButton.getAttribute('data-quantity-adjust'));
      const { min, max } = getQuantityBounds(input);
      input.value = Math.min(max, Math.max(min, Number(input.value || min) + delta));
      clampQuantityInput(input);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const cartMainAdjust = event.target.closest('[data-cart-main-adjust]');
    if (cartMainAdjust) {
      const key = cartMainAdjust.dataset.key;
      const delta = parseInt(cartMainAdjust.dataset.cartMainAdjust);
      const container = document.querySelector(`[data-cart-main-qty="${key}"]`);
      const display = container?.querySelector('span');
      const newQty = Math.max(0, parseInt(display?.textContent || '1') + delta);
      container?.querySelectorAll('button').forEach((b) => { b.disabled = true; });
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: newQty }),
      }).then(() => location.reload());
    }
  });

  document.addEventListener('keydown', (event) => {
    trapFocus(event);
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-drawer]:not([hidden]), [data-cart-drawer]:not([hidden])').forEach((drawer) => closeOverlay(drawer));
    closeSearchDrawer();
  });

  document.addEventListener('input', (event) => {
    if (!event.target.matches?.('[data-product-quantity]')) return;
    updateQuantityButtons(event.target);
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches?.('[data-product-quantity]')) return;
    clampQuantityInput(event.target);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!form.matches?.('.product-form')) return;
    if (form.dataset.cartInventoryChecked === 'true') {
      delete form.dataset.cartInventoryChecked;
      return;
    }

    const input = form.querySelector('[data-product-quantity]');
    if (!input || !Number.isFinite(getProductInventoryMax(input))) return;

    event.preventDefault();
    try {
      const remaining = await syncProductQuantityCartLimit(input);
      if (remaining <= 0 || Number(input.value || 0) > remaining) return;
      form.dataset.cartInventoryChecked = 'true';
      const submitButton = form.querySelector('[type="submit"][name="add"]');
      form.requestSubmit(submitButton);
    } catch {
      form.dataset.cartInventoryChecked = 'true';
      const submitButton = form.querySelector('[type="submit"][name="add"]');
      form.requestSubmit(submitButton);
    }
  });

  document.addEventListener('shopify:section:load', () => {
    initScrollAnimations();
    initReadMore();
    initProductQuantities();
  });

  function initScrollAnimations() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const SELECTORS = [
      '.section-header',
      '.feature-card',
      '.trust-bar__item',
      '.collection-card',
      '.comparison__panel',
      '.accordion details',
      '.testimonial-card',
      '.split > div',
      '.split > .media-frame',
      '.footer__brand',
      '.footer__nav',
    ];

    const candidates = document.querySelectorAll(SELECTORS.join(', '));
    const byParent = new Map();

    candidates.forEach((el) => {
      if (el.classList.contains('kong-reveal')) return;
      el.classList.add('kong-reveal');
      const parent = el.parentElement;
      if (!byParent.has(parent)) byParent.set(parent, []);
      byParent.get(parent).push(el);
    });

    byParent.forEach((siblings) => {
      siblings.forEach((el, i) => {
        el.style.transitionDelay = `${i * 80}ms`;
      });
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -32px 0px' }
    );

    document.querySelectorAll('.kong-reveal').forEach((el) => observer.observe(el));
  }

  function initReadMore() {
    if (window.innerWidth > 749) return;

    document.querySelectorAll('.rte--collapsible').forEach((rte) => {
      const btn = document.querySelector(`[aria-controls="${rte.id}"]`);
      if (!btn) return;

      if (rte.scrollHeight <= rte.offsetHeight + 4) {
        btn.hidden = true;
        return;
      }

      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        rte.classList.toggle('is-expanded', !expanded);
        const label = btn.querySelector('[data-label]');
        if (label) label.textContent = expanded ? 'Mehr lesen' : 'Weniger';
      });
    });
  }

  function initProductQuantities() {
    document.querySelectorAll('[data-product-quantity]').forEach((input) => {
      syncProductQuantityCartLimit(input).catch(() => clampQuantityInput(input));
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initScrollAnimations();
    initReadMore();
    initPredictiveSearch();
    initProductQuantities();
  });

  function closeSearchDrawer() {
    const drawer = document.getElementById('SearchDrawer');
    if (!drawer || drawer.hidden) return;
    const panel = drawer.querySelector('.search-drawer__panel');
    const results = document.getElementById('SearchResults');
    const input = document.getElementById('SearchInput');
    if (input) input.value = '';
    if (results) { results.innerHTML = ''; results.hidden = true; }
    if (panel) {
      panel.dataset.closing = '';
      panel.addEventListener('animationend', () => {
        delete panel.dataset.closing;
        finishCloseOverlay(drawer);
      }, { once: true });
    } else {
      finishCloseOverlay(drawer);
    }
  }

  function initPredictiveSearch() {
    const input = document.getElementById('SearchInput');
    const results = document.getElementById('SearchResults');
    if (!input || !results) return;

    let timer;

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) { results.hidden = true; results.innerHTML = ''; return; }

      results.innerHTML = '<li class="search-drawer__loading">Suche läuft…</li>';
      results.hidden = false;

      timer = setTimeout(async () => {
        try {
          const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,article&resources[limit]=6&resources[options][unavailable_products]=last`;
          const data = await (await fetch(url)).json();
          renderSearchResults(data.resources.results, q, results);
        } catch { results.hidden = true; }
      }, 260);
    });
  }

  function renderSearchResults(res, query, container) {
    const products = res.products || [];
    const articles = res.articles || [];
    if (!products.length && !articles.length) {
      container.innerHTML = `<li class="search-drawer__no-results">Keine Ergebnisse für „${query}"</li>`;
      container.hidden = false;
      return;
    }

    const items = [
      ...products.map((p) => `
        <li>
          <a class="search-drawer__result" href="${p.url}" role="option">
            ${p.featured_image?.url
              ? `<img class="search-drawer__result-image" src="${p.featured_image.url}&width=120" alt="" width="48" height="48" loading="lazy">`
              : ''}
            <div class="search-drawer__result-info">
              <span class="search-drawer__result-title">${p.title}</span>
              ${p.price ? `<span class="search-drawer__result-price">${formatMoney(p.price)}</span>` : ''}
            </div>
          </a>
        </li>`),
      ...articles.map((a) => `
        <li>
          <a class="search-drawer__result" href="${a.url}" role="option">
            <div class="search-drawer__result-info">
              <span class="search-drawer__result-badge">Artikel</span>
              <span class="search-drawer__result-title">${a.title}</span>
            </div>
          </a>
        </li>`),
      `<li>
        <a class="search-drawer__all" href="/search?q=${encodeURIComponent(query)}">
          Alle Ergebnisse für „${query}" →
        </a>
      </li>`,
    ];

    container.innerHTML = items.join('');
    container.hidden = false;
  }

  function formatMoney(price) {
    return parseFloat(price).toLocaleString('de-AT', { style: 'currency', currency: 'EUR' });
  }

  function cartMoney(cents) {
    return (cents / 100).toLocaleString('de-AT', { style: 'currency', currency: 'EUR' });
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count > 0 ? count : '';
      el.hidden = count === 0;
    });
  }

  async function loadCartDrawer() {
    const content = document.querySelector('[data-cart-drawer-content]');
    if (!content) return;
    try {
      const cart = await (await fetch('/cart.js')).json();
      renderCartDrawerContent(cart, content);
    } catch {
      content.innerHTML = '<p style="padding:var(--space-4)">Fehler beim Laden.</p>';
    }
  }

  function renderCartDrawerContent(cart, content) {
    if (cart.item_count === 0) {
      content.innerHTML = `
        <div class="cart-drawer__empty">
          <p>Dein Warenkorb ist leer.</p>
          <a class="button" href="/collections/all">Weiter shoppen</a>
        </div>`;
      return;
    }

    const items = cart.items.map((item) => `
      <div class="cart-drawer__item">
        ${item.image
          ? `<a href="${item.url}"><img class="cart-drawer__item-img" src="${item.image}&width=200" alt="${escHtml(item.title)}" width="72" height="72" loading="eager" fetchpriority="high"></a>`
          : ''}
        <div class="cart-drawer__item-body">
          <a class="cart-drawer__item-title" href="${item.url}">${escHtml(item.product_title)}</a>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-drawer__item-variant">${escHtml(item.variant_title)}</p>`
            : ''}
          <div class="cart-drawer__item-row">
            <div class="quantity">
              <button type="button" data-cart-adjust="-1" data-key="${item.key}" aria-label="Weniger">−</button>
              <input type="number" value="${item.quantity}" min="0" data-cart-qty data-key="${item.key}" readonly>
              <button type="button" data-cart-adjust="1" data-key="${item.key}" aria-label="Mehr"${item.inventory_policy === 'deny' && Number.isFinite(item.inventory_quantity) && item.quantity >= item.inventory_quantity ? ' disabled aria-disabled="true"' : ''}>+</button>
            </div>
            <span class="cart-drawer__item-price">${cartMoney(item.final_line_price)}</span>
          </div>
        </div>
      </div>`).join('');

    content.innerHTML = `
      <div class="cart-drawer__scroll">
        <div class="cart-drawer__items">${items}</div>
        <a class="cart-drawer__promo" href="/cart" aria-label="Gratis-Tier Aktion einlösen">
          <span class="cart-drawer__promo-bg-text" aria-hidden="true">GRATIS</span>
          <span class="cart-drawer__promo-icon" aria-hidden="true">🎁</span>
          <span class="cart-drawer__promo-body">
            <strong class="cart-drawer__promo-heading">Gratis Tier ab 1000€</strong>
            <span class="cart-drawer__promo-text">Gratis-Produkt im Warenkorb einlösen</span>
          </span>
          <span class="cart-drawer__promo-arrow" aria-hidden="true">→</span>
        </a>
      </div>
      <div class="cart-drawer__footer">
        <div class="cart-drawer__total">
          <span>Gesamt</span>
          <strong>${cartMoney(cart.total_price)}</strong>
        </div>
        <a class="button" href="/checkout">Zur Kasse →</a>
        <a class="cart-drawer__viewcart" href="/cart">Warenkorb ansehen</a>
      </div>`;

    content.querySelectorAll('[data-cart-adjust]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const input = content.querySelector(`[data-cart-qty][data-key="${key}"]`);
        const newQty = Math.max(0, (parseInt(input?.value) || 0) + parseInt(btn.dataset.cartAdjust));
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: newQty }),
        });
        const updated = await (await fetch('/cart.js')).json();
        updateCartCount(updated.item_count);
        renderCartDrawerContent(updated, content);
      });
    });
  }

})();
